import React from "react";

const CloseIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Shared "Create webhook" modal used by both the Settings → Webhooks page and
 * the Tasks (new/edit) form, so the create UI stays identical everywhere.
 */
function CreateWebhookModal({
  open,
  name,
  onNameChange,
  creating = false,
  error = "",
  onCreate,
  onClose,
}) {
  if (!open) return null;

  const close = () => {
    if (creating) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={close}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[440px] max-w-[92vw] p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-[16px] font-semibold text-[#0f172a]">
              Create webhook
            </h3>
            <p className="text-[13px] text-[#757575] mt-1">
              Create a new webhook endpoint to receive events from external
              services.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            disabled={creating}
            className="text-[#757575] hover:text-black disabled:opacity-50"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-4">
          <label className="text-[13px] font-medium block mb-2">
            Webhook name
          </label>
          <input
            autoFocus
            type="text"
            value={name}
            disabled={creating}
            onChange={(event) => onNameChange?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onCreate?.();
              }
            }}
            placeholder="e.g. Fireflies integration"
            className="w-full h-10 border !border-[#D9D9D9] rounded-md px-3 text-[14px] outline-none focus:border-[#757575] disabled:bg-[#f1f5f9]"
          />
          <p className="text-[12px] text-[#757575] mt-2">
            Give your webhook a descriptive name to identify it later.
          </p>
          {error && (
            <small className="text-red-500 block mt-2">{error}</small>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={close}
            disabled={creating}
            className="h-11 px-4 rounded-md border !border-[#D9D9D9] text-[13px] text-[#000000] bg-white hover:bg-[#f8fafc] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name?.trim() || creating}
            onClick={onCreate}
            className={`h-11 px-5 rounded-md text-[13px] text-white font-medium ${name?.trim() && !creating
              ? "bg-black hover:bg-gray-800"
              : "bg-[#666666] cursor-not-allowed"
              }`}
          >
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateWebhookModal;
